import React, { useMemo, useState } from 'react'
import { Pagination } from '@/modules/shared/components/Pagination'
import { useBusinessTripRatesTableContext } from '../context/BusinessTripRatesTableContext'
import { BusinessTripRatesRowProvider } from '../context/BusinessTripRatesRowContext'
import { BusinessTripRatesTableRow } from './BusinessTripRatesTableRow'
import { withDefaultErrorBoundary } from '@/modules/shared/hoc/withDefaultErrorBoundary'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  DEFAULT_SORT_KEY,
  DEFAULT_SORT_STATE,
  nextSortState,
  SortKey,
  sortRates,
} from '../sorting'

const DEFAULT_PER_PAGE = 20

const COLUMNS: Array<TableHeaderColumn & { key: SortKey | null }> = [
  { title: 'Państwo', key: 'countryCode', type: 'string', sort: true },
  { title: 'Data od', key: 'dateFrom', type: 'date', sort: true },
  { title: 'Data do', key: 'dateTo', type: 'date', sort: true },
  { title: 'Dieta', key: 'allowanceRate', type: 'number', sort: true },
  {
    title: 'Nocleg',
    key: 'allowanceAccomodationRate',
    type: 'number',
    sort: true,
  },
  {
    title: 'Sanitariaty',
    key: 'allowanceSanitarityRate',
    type: 'number',
    sort: true,
  },
  {
    title: 'Limit hotelowy',
    key: 'allowanceHotelLimitationRate',
    type: 'number',
    sort: true,
  },
  { title: '', key: null, type: 'string', sort: false },
]

const TABLE_CLASS =
  'tw-relative tw-w-full tw-table-auto ' +
  '[&_td]:tw-border-r [&_td]:tw-border-base-300 ' +
  '[&_th]:tw-border-r [&_th]:tw-border-base-300 ' +
  '[&_td:last-child]:tw-border-r-0 [&_th:last-child]:tw-border-r-0'

export const BusinessTripRatesTable = withDefaultErrorBoundary(() => {
  const { rates } = useBusinessTripRatesTableContext()
  const { t } = useLocalize()

  const [page, setPage] = useState<number>(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE)
  const [sort, setSort] = useState(DEFAULT_SORT_STATE)

  const handleHeaderClick = (key: string) => {
    setSort((current) => nextSortState(current, key as SortKey))
    setPage(1)
  }

  const rows = useMemo(() => {
    const data = sortRates(rates, sort.key, sort.asc)

    const safePage = Math.max(page || 1, 1)
    const safePerPage = Math.max(perPage || data.length || 1, 1)
    const start = (safePage - 1) * safePerPage
    const end = start + safePerPage

    return data.slice(start, end)
  }, [rates, page, perPage, sort])

  return (
    <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-2">
      {rows.length > 0 ? (
        <>
          <div
            className="tw-min-h-0 tw-flex-1 tw-overflow-auto"
            data-test-id="table-business-trip-rates"
          >
            <table className={TABLE_CLASS}>
              <TableHeader
                opacity
                rounded={false}
                disabledCheckboxToggle
                sticky
                columns={COLUMNS}
                sortConfig={
                  sort.key === DEFAULT_SORT_KEY
                    ? null
                    : { key: sort.key, direction: sort.asc ? 'asc' : 'desc' }
                }
                onHeaderClick={handleHeaderClick}
              />
              <tbody>
                {rows.map((rate) => (
                  <BusinessTripRatesRowProvider key={rate.id} rate={rate}>
                    <BusinessTripRatesTableRow />
                  </BusinessTripRatesRowProvider>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={page}
            totalItems={rates.length}
            pageSize={perPage}
            onPageChange={setPage}
            onPageSizeChange={setPerPage}
          />
        </>
      ) : (
        <>
          <table className={TABLE_CLASS}>
            <TableHeader
              opacity
              disabledCheckboxToggle
              columns={COLUMNS}
              sortConfig={null}
            />
          </table>
          <TableEmpty title={t('common.no-data-to-display')} />
        </>
      )}
    </section>
  )
})
